<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="500"
  >
    <v-card title="Create Repository">
      <v-card-text>
        <v-text-field
          v-model="newRepoName"
          label="Repository Name"
          variant="outlined"
          density="compact"
          autofocus
        />
        <div v-if="normalizedRepoName !== trimmedRepoName" class="repo-hint">
          <v-icon
            color="success"
            size="18"
            style="margin-right: 4px"
          >
            mdi-check-circle
          </v-icon>
          <div>
            <span>Your new repository will be created as {{ normalizedRepoName }}.</span><br/>
            <span>The repository name can only contain ASCII letters, digits, and the characters ., -, and _.</span>
          </div>
        </div>
        <v-textarea
          v-model="newRepoDescription"
          label="Description"
          rows="2"
          variant="outlined"
          density="compact"
        />
        <v-radio-group v-model="newRepoIsPrivate" row>
          <v-radio label="Public" :value="false" />
          <v-radio label="Private" :value="true" />
        </v-radio-group>
      </v-card-text>
      <v-card-actions>
        <v-btn @click="toggleDialog(false)">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="createRepo"
          :loading="isLoadingCreate"
          :disabled="!newRepoName"
        >
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { useStore } from 'vuex'
import { ref, computed } from 'vue'
import gitHubApi from '@/api/gitHubApi'

const props = defineProps({
  modelValue: Boolean,
  githubAccountLogin: String
})

const emit = defineEmits(['update:modelValue', 'onRepoCreated'])

const store = useStore()
const newRepoName = ref('')
const newRepoDescription = ref('')
const newRepoIsPrivate = ref(false)
const isLoadingCreate = ref(false)

const normalizedRepoName = computed(() => {
  return newRepoName.value.trim().replace(/\s+/g, '-')
})

const trimmedRepoName = computed(() => {
  return newRepoName.value.trim()
})

const resetState = () => {
  newRepoName.value = ''
  newRepoDescription.value = ''
  newRepoIsPrivate.value = false
}

const toggleDialog = (value) => {
  if (!value) {
    resetState()
  }

  emit('update:modelValue', false)
}

const createRepo = async () => {
  try {
    isLoadingCreate.value = true
    const response = await gitHubApi.createGitHubRepo(
      props.githubAccountLogin,
      normalizedRepoName.value,
      newRepoDescription.value,
      newRepoIsPrivate.value
    )

    if (response.success) {
      const newRepo = response.resData
      await gitHubApi.initGitHubRepo(
        props.githubAccountLogin,
        props.githubAccountLogin,
        normalizedRepoName.value,
        'main',
        '.gitkeep',
        '',
        'feat: init repo'
      )

      emit('onRepoCreated', newRepo)
      store.commit('snackbar/showMessage', {
        content: newRepo.name + ' Repository has been created successfully',
        timeout: 3000,
        color: 'green'
      }, { root: true })

      toggleDialog(false)
    } else {
      store.commit('snackbar/showMessage', {
        content: response.errorText,
        timeout: 3000,
        color: 'red'
      }, { root: true })
    }
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: e.message,
      timeout: 3000,
      color: 'red'
    }, { root: true })
  } finally {
    isLoadingCreate.value = false
  }
}
</script>

<style scoped>
.repo-hint {
  margin-top: -18px;
  margin-bottom: 4px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  line-height: 1.4;
  white-space: pre-line;
}

.repo-hint span:first-child {
  color: #1a7f37;
}

.repo-hint .v-icon {
  margin-right: 4px;
}
</style>
